import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

// Not a full-bleed band but one enormous 32px pastel plate, inset from the
// viewport on all sides so the warm paper ground still frames it.
export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    const linkCls = "text-sm text-dim hover:text-accent transition-colors duration-200";

    return (
        <footer className="mt-24 pb-6" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8">
                <div className="rounded-3xl bg-surface-raised px-6 sm:px-10 lg:px-14 py-14">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                        {/* Brand / contact */}
                        <div className="col-span-2 lg:col-span-1">
                            <div className="flex items-center gap-2.5">
                                <span className="grid place-items-center w-9 h-9 rounded-full bg-accent-sheen">
                                    <span className="w-3 h-3 rounded-full bg-surface" />
                                </span>
                                <span className="font-display text-lg font-semibold tracking-[-0.01em]">{brand.site_name}</span>
                            </div>
                            {brand.tagline && <p className="mt-5 text-sm text-dim leading-relaxed max-w-xs">{brand.tagline}</p>}
                            <p className="mt-6 inline-flex rounded-full bg-surface px-4 py-2 font-mono text-xs text-dim" data-testid="footer-contact-email">
                                {brand.email}
                            </p>
                            {brand.phone && (
                                <p className="mt-2 inline-flex rounded-full bg-surface px-4 py-2 font-mono text-xs text-dim">{brand.phone}</p>
                            )}
                        </div>

                        {/* Sections */}
                        <div>
                            <h5 className="eyebrow text-accent mb-5">Sections</h5>
                            <ul className="space-y-3">
                                {CATEGORIES.map((c) => (
                                    <li key={c}>
                                        <Link to={`/category/${c}`} className={linkCls}>
                                            {CATEGORY_LABELS[c]}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h5 className="eyebrow text-accent mb-5">Company</h5>
                            <ul className="space-y-3">
                                <li><Link to="/advertise" data-testid="footer-advertise" className={linkCls}>Advertise with us</Link></li>
                                <li><Link to="/archive" className={linkCls}>Archive</Link></li>
                                <li><Link to="/privacy" data-testid="footer-privacy" className={linkCls}>Privacy Policy</Link></li>
                                <li><Link to="/terms" data-testid="footer-terms" className={linkCls}>Terms of Use</Link></li>
                            </ul>
                        </div>

                        {/* Newsletter */}
                        <div className="col-span-2 lg:col-span-1 rounded-2xl bg-surface shadow-card p-7">
                            <h5 className="eyebrow text-accent mb-3" data-testid="footer-newsletter-title">Newsletter</h5>
                            <p className="text-sm text-dim leading-relaxed">Get the latest financial news delivered to your inbox.</p>
                            <Link
                                to="/register"
                                data-testid="footer-newsletter-subscribe"
                                className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-6 py-3 text-[13px] font-semibold shadow-card hover:shadow-glow hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
                            >
                                Subscribe
                                <ArrowUpRight size={14} />
                            </Link>
                            <button
                                onClick={() => setUnsubOpen(true)}
                                data-testid="footer-unsubscribe"
                                className="mt-5 block text-xs text-dim hover:text-neg-ink transition-colors duration-200 link-underline"
                            >
                                Unsubscribe
                            </button>
                        </div>
                    </div>

                    <div className="rule mt-14" />
                    <p className="mt-7 font-mono text-xs text-dim">
                        © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                    </p>
                </div>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
